// helper functions to calculate stats for overview
import { Key, LessonStatus } from "../constants/enums";
import { AnalyticsData } from "../types/data";
import { BiteStatus } from "../types/enums";
import dayjs from "dayjs";

export const formatMs = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);

  return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
};

// epoch time to formatted time "Dec 5, 2025  2:45 PM"
export const formatDate = (date: string | number): string => {
  let parsedDate = date;
  if (typeof date === "string" && !isNaN(Number(date))) {
    parsedDate = Number(date);
  }
  return dayjs(parsedDate).format("MMM D, YYYY\th:mm A");
};

export const calculateTotalStudents = (data: AnalyticsData[]): number => {
  return new Set(data.map((row) => row[Key.STUDENT_NAME].value)).size;
};

export const calculateTotalLessons = (data: AnalyticsData[]): number => {
  return data.length;
};

export const calculateLessonsStarted = (data: AnalyticsData[]): number => {
  return data.filter(
    (row) =>
      row[Key.LESSON_STATUS] === LessonStatus.STARTED ||
      row[Key.LESSON_STATUS] === LessonStatus.COMPLETED,
  ).length;
};

export const calculateLessonsCompleted = (data: AnalyticsData[]): number => {
  return data.filter((row) => row[Key.LESSON_STATUS] === LessonStatus.COMPLETED)
    .length;
};

export const calculateAverageTimeSpent = (data: AnalyticsData[]): number => {
  const startedLessons = data.filter(
    (row) =>
      row[Key.LESSON_STATUS] === LessonStatus.STARTED ||
      row[Key.LESSON_STATUS] === LessonStatus.COMPLETED,
  );

  if (startedLessons.length === 0) return 0;

  return (
    startedLessons.reduce((sum, row) => sum + row[Key.TIME_SPENT], 0) /
    startedLessons.length
  );
};

export const calculateCompletionRate = (data: AnalyticsData[]): number => {
  if (data.length === 0) return 0;

  // Assessment-only lessons carry no bites, so they have no completion ratio.
  // Counting them as 0% dragged the class average down for work the student
  // could not have done — they are excluded from the denominator instead.
  const sessionRates = data
    .filter((row) => (row[Key.BITES] || []).length > 0)
    .map((row) => {
      const bites = row[Key.BITES];
      const completedBites = bites.filter(
        (bite) => bite.biteStatus === BiteStatus.COMPLETED,
      ).length;

      return completedBites / bites.length;
    });

  if (sessionRates.length === 0) return 0;

  return (
    sessionRates.reduce((sum, rate) => sum + rate, 0) / sessionRates.length
  );
};
